import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaUtensils, FaDumbbell, FaEdit, FaTrash, FaCheck, FaSpinner, FaSyncAlt, FaBookOpen, FaSearch, FaPlus } from 'react-icons/fa';
import { toast } from 'sonner';
import axios from 'axios';
import CloseMacroCardWarning from '@/components/CloseMacroCardWarning';
import PlanMacrosSummary from '@/components/PlanMacrosSummary';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Auto-includes admin Bearer token on every request within this component
const adminApi = axios.create();
adminApi.interceptors.request.use(cfg => {
  const t = localStorage.getItem('deisycfit_admin_token');
  if (t) cfg.headers['Authorization'] = `Bearer ${t}`;
  return cfg;
});

/**
 * Modal para que el admin vea y edite la dieta y rutina generada
 * de una clienta específica (Fit Evolution).
 */
export default function UserPlanViewer({ email, isOpen, onClose }) {
  const [tab, setTab] = useState('diet');
  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState(null);

  useEffect(() => {
    if (isOpen && email) fetchPlans();
  }, [isOpen, email]);

  const fetchPlans = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await adminApi.get(`${API}/admin/user-plans/${encodeURIComponent(email)}`);
      setPlans(data);
    } catch (e) {
      toast.error('Error cargando planes: ' + (e.response?.data?.detail || e.message));
    } finally {
      if (!silent) setLoading(false);
    }
  };

  // Refresh silencioso: usado desde acciones (eliminar/editar/agregar/aplicar plantilla)
  // para NO desmontar WorkoutPanel/DietPanel y preservar el scroll y el día visible.
  const silentRefresh = () => fetchPlans({ silent: true });

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        data-testid="user-plan-viewer-modal"
        className="fixed inset-0 z-[100] flex items-center justify-center p-4"
        style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(8px)' }}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.95, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.95, y: 20 }}
          className="relative w-full max-w-6xl max-h-[92vh] rounded-2xl overflow-hidden flex flex-col"
          style={{ background: 'linear-gradient(180deg, #0f172a, #1e1b4b)', border: '1px solid rgba(255,255,255,0.1)' }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="p-5 border-b border-white/10 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-white">
                📋 Plan de {plans?.user?.name || email}
              </h2>
              <p className="text-xs text-slate-400 mt-1">{email}</p>
            </div>
            <button
              onClick={onClose}
              data-testid="close-plan-viewer"
              className="w-9 h-9 rounded-full flex items-center justify-center text-white hover:bg-white/10 transition-all"
            >
              <FaTimes />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex border-b border-white/10 bg-slate-900/50">
            <button
              onClick={() => setTab('diet')}
              data-testid="tab-diet"
              className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 font-bold transition-all ${tab === 'diet' ? 'bg-pink-500/20 text-pink-300 border-b-2 border-pink-500' : 'text-slate-400 hover:text-white'}`}
            >
              <FaUtensils /> Dieta ({plans?.diet_batches?.length || 0} comidas)
            </button>
            <button
              onClick={() => setTab('workout')}
              data-testid="tab-workout"
              className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 font-bold transition-all ${tab === 'workout' ? 'bg-cyan-500/20 text-cyan-300 border-b-2 border-cyan-500' : 'text-slate-400 hover:text-white'}`}
            >
              <FaDumbbell /> Rutina ({plans?.workout?.exercises?.length || 0} ejercicios)
            </button>
          </div>

          {/* Contenido */}
          <div className="flex-1 overflow-y-auto p-5">
            {loading ? (
              <div className="flex items-center justify-center py-20 text-slate-400">
                <FaSpinner className="animate-spin mr-2" /> Cargando planes...
              </div>
            ) : tab === 'diet' ? (
              <DietPanel batches={plans?.diet_batches || []} email={email} onRefresh={silentRefresh} />
            ) : (
              <WorkoutPanel workout={plans?.workout} email={email} userSex={plans?.user?.sex || ''} onRefresh={silentRefresh} />
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─────────────────────────────────────────────────────────
// DIETA
// ─────────────────────────────────────────────────────────
function DietPanel({ batches, email, onRefresh }) {
  if (!batches.length) {
    return <p className="text-center text-slate-400 py-8">No hay recetas generadas todavía.</p>;
  }
  return (
    <div className="space-y-6">
      <PlanMacrosSummary batches={batches} title="🎯 Macros totales del plan" testIdPrefix="admin-plan-total" />
      {batches.map((batch) => (
        <div key={batch.id} className="rounded-xl p-4" style={{ background: 'rgba(var(--brand-primary-glow), 0.08)', border: '1px solid rgba(var(--brand-primary-glow), 0.25)' }}>
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-bold text-pink-300">🍽️ {batch.meal_time}</h3>
            <span className="text-xs text-slate-400">
              {batch.target_calories} kcal · {batch.recipes?.length || 0} recetas
            </span>
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            {(batch.recipes || []).map((recipe) => (
              <RecipeCard
                key={recipe.id}
                recipe={recipe}
                batchId={batch.id}
                email={email}
                mealTime={batch.meal_style || batch.meal_slot || batch.meal_time}
                onRefresh={onRefresh}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function RecipeCard({ recipe, batchId, email, mealTime, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const [form, setForm] = useState({
    name: recipe.name,
    calories: recipe.calories,
    protein: recipe.protein,
    carbs: recipe.carbs,
    fats: recipe.fats,
    prep_minutes: recipe.prep_minutes,
    ingredients: recipe.ingredients || [],
    instructions: recipe.instructions || [],
  });

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.put(`${API}/admin/user-plans/${encodeURIComponent(email)}/recipe/${batchId}/${recipe.id}`, form);
      toast.success('Receta actualizada');
      setEditing(false);
      onRefresh();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    if (!window.confirm(`¿Regenerar "${recipe.name}" con una receta nueva? Se respetarán las restricciones del perfil (alergias, alimentos excluidos).`)) return;
    setRegenerating(true);
    try {
      const { data } = await adminApi.post(`${API}/admin/user-plans/${encodeURIComponent(email)}/recipe/${batchId}/${recipe.id}/regenerate`);
      if (data?.macro_within_tolerance === false) {
        toast(`Receta regenerada, pero sus macros no quedaron dentro del ±10% del objetivo${data.macro_note ? `: ${data.macro_note}` : ''}.`, { icon: '⚠️', duration: 6000 });
      } else {
        toast.success('Receta regenerada exitosamente');
      }
      onRefresh();
    } catch (e) {
      toast.error('Error regenerando: ' + (e.response?.data?.detail || e.message));
    } finally {
      setRegenerating(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`¿Eliminar la receta "${recipe.name}"?`)) return;
    try {
      await adminApi.delete(`${API}/admin/user-plans/${encodeURIComponent(email)}/recipe/${batchId}/${recipe.id}`);
      toast.success('Receta eliminada');
      onRefresh();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="rounded-lg p-3 bg-slate-800/60 border border-white/5" data-testid={`recipe-card-${recipe.id}`}>
      {!editing ? (
        <>
          <div className="flex items-start justify-between gap-2 mb-2">
            <h4 className="font-bold text-white text-sm">{recipe.name}</h4>
            <div className="flex gap-1 flex-shrink-0">
              <button
                onClick={() => setShowLibraryPicker(true)}
                data-testid={`pick-from-library-${recipe.id}`}
                className="w-7 h-7 rounded flex items-center justify-center bg-pink-500/20 text-pink-300 hover:bg-pink-500/30"
                title="Elegir receta desde la biblioteca"
              >
                <FaBookOpen className="text-xs" />
              </button>
              <button
                onClick={regenerate}
                disabled={regenerating}
                data-testid={`regenerate-recipe-${recipe.id}`}
                className="w-7 h-7 rounded flex items-center justify-center bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 disabled:opacity-50"
                title="Regenerar receta con nueva creación (respeta alergias y no-gustan)"
              >
                {regenerating ? <FaSpinner className="text-xs animate-spin" /> : <FaSyncAlt className="text-xs" />}
              </button>
              <button
                onClick={() => {
                  setForm({
                    name: recipe.name,
                    calories: recipe.calories,
                    protein: recipe.protein,
                    carbs: recipe.carbs,
                    fats: recipe.fats,
                    prep_minutes: recipe.prep_minutes,
                    ingredients: recipe.ingredients || [],
                    instructions: recipe.instructions || [],
                  });
                  setEditing(true);
                }}
                data-testid={`edit-recipe-${recipe.id}`}
                className="w-7 h-7 rounded flex items-center justify-center bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30"
                title="Editar"
              >
                <FaEdit className="text-xs" />
              </button>
              <button
                onClick={remove}
                data-testid={`delete-recipe-${recipe.id}`}
                className="w-7 h-7 rounded flex items-center justify-center bg-red-500/20 text-red-300 hover:bg-red-500/30"
                title="Eliminar"
              >
                <FaTrash className="text-xs" />
              </button>
            </div>
          </div>
          <div className="text-xs text-slate-400 mb-2 flex flex-wrap gap-2">
            <span>🔥 {recipe.calories} kcal</span>
            <span>🥩 {recipe.protein}g P</span>
            <span>🌾 {recipe.carbs}g C</span>
            <span>🥑 {recipe.fats}g G</span>
            <span>⏱️ {recipe.prep_minutes}min</span>
          </div>
          {/* 2026-10-13 · Mismo aviso "Agregar un alimento" que el dashboard de la clienta. */}
          <CloseMacroCardWarning key={`${recipe.id}:${recipe.replaced_at || ''}`} recipe={recipe} batchId={batchId} adminEmail={email} onUpdated={() => onRefresh()} />
          {recipe.ingredients?.length > 0 && (
            <details className="text-xs text-slate-300">
              <summary className="cursor-pointer text-pink-300 mb-1">Ingredientes ({recipe.ingredients.length})</summary>
              <ul className="ml-4 list-disc space-y-0.5">
                {recipe.ingredients.map((ing, i) => (
                  <li key={i}>{typeof ing === 'string' ? ing : `${ing.name} — ${ing.amount}`}</li>
                ))}
              </ul>
            </details>
          )}
          {recipe.instructions?.length > 0 && (
            <details className="text-xs text-slate-300 mt-1">
              <summary className="cursor-pointer text-pink-300 mb-1">Preparación</summary>
              <ol className="ml-4 space-y-1">
                {recipe.instructions.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            </details>
          )}
        </>
      ) : (
        <div className="space-y-2">
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-2 py-1 rounded bg-slate-900 text-white text-sm border border-slate-700"
            placeholder="Nombre"
            data-testid={`edit-recipe-name-${recipe.id}`}
          />
          <div className="grid grid-cols-5 gap-1">
            <input type="number" value={form.calories} onChange={(e) => setForm({ ...form, calories: parseInt(e.target.value) || 0 })} className="px-1 py-1 rounded bg-slate-900 text-white text-xs border border-slate-700" placeholder="kcal" title="Calorías" />
            <input type="number" value={form.protein} onChange={(e) => setForm({ ...form, protein: parseInt(e.target.value) || 0 })} className="px-1 py-1 rounded bg-slate-900 text-white text-xs border border-slate-700" placeholder="P" title="Proteína" />
            <input type="number" value={form.carbs} onChange={(e) => setForm({ ...form, carbs: parseInt(e.target.value) || 0 })} className="px-1 py-1 rounded bg-slate-900 text-white text-xs border border-slate-700" placeholder="C" title="Carbohidratos" />
            <input type="number" value={form.fats} onChange={(e) => setForm({ ...form, fats: parseInt(e.target.value) || 0 })} className="px-1 py-1 rounded bg-slate-900 text-white text-xs border border-slate-700" placeholder="G" title="Grasas" />
            <input type="number" value={form.prep_minutes} onChange={(e) => setForm({ ...form, prep_minutes: parseInt(e.target.value) || 0 })} className="px-1 py-1 rounded bg-slate-900 text-white text-xs border border-slate-700" placeholder="min" title="Minutos" />
          </div>
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wider">Ingredientes (uno por línea, formato: Nombre — cantidad)</label>
            <textarea
              value={(form.ingredients || []).map((i) => typeof i === 'string' ? i : `${i.name} — ${i.amount}`).join('\n')}
              onChange={(e) => {
                const lines = e.target.value.split('\n').filter(Boolean).map((line) => {
                  const parts = line.split(/[—-]/);
                  return parts.length >= 2
                    ? { name: parts[0].trim(), amount: parts.slice(1).join('—').trim() }
                    : { name: line.trim(), amount: '' };
                });
                setForm({ ...form, ingredients: lines });
              }}
              className="w-full px-2 py-1 rounded bg-slate-900 text-white text-xs border border-slate-700 font-mono"
              rows={4}
            />
          </div>
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wider">Preparación (un paso por línea)</label>
            <textarea
              value={(form.instructions || []).join('\n')}
              onChange={(e) => setForm({ ...form, instructions: e.target.value.split('\n').filter(Boolean) })}
              className="w-full px-2 py-1 rounded bg-slate-900 text-white text-xs border border-slate-700"
              rows={4}
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button
              onClick={() => setEditing(false)}
              className="px-3 py-1 text-xs rounded bg-slate-700 text-white hover:bg-slate-600"
            >
              Cancelar
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid={`save-recipe-${recipe.id}`}
              className="px-3 py-1 text-xs rounded bg-green-600 text-white hover:bg-green-500 flex items-center gap-1"
            >
              {saving ? <FaSpinner className="animate-spin" /> : <FaCheck />}
              Guardar
            </button>
          </div>
        </div>
      )}

      {showLibraryPicker && (
        <LibraryPickerModal
          mealTime={mealTime}
          currentRecipeId={recipe.id}
          onClose={() => setShowLibraryPicker(false)}
          onPick={async (libraryRecipeId) => {
            try {
              const { data } = await adminApi.post(
                `${API}/admin/user-plans/${encodeURIComponent(email)}/recipe/${batchId}/${recipe.id}/replace-from-library`,
                { library_recipe_id: libraryRecipeId }
              );
              if (data?.macro_within_tolerance === false) {
                toast(`Receta reemplazada, pero sus macros no quedaron dentro del ±10% del objetivo${data.macro_note ? `: ${data.macro_note}` : ''}.`, { icon: '⚠️', duration: 6000 });
              } else {
                toast.success('Receta reemplazada desde la biblioteca');
              }
              setShowLibraryPicker(false);
              onRefresh();
            } catch (e) {
              toast.error('Error: ' + (e.response?.data?.detail || e.message));
            }
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// MODAL: Seleccionar receta desde la biblioteca central
// ─────────────────────────────────────────────────────────
function LibraryPickerModal({ mealTime, currentRecipeId, onClose, onPick }) {
  // Inferir el momento a partir del meal_style / meal_slot / meal_time del batch.
  // Sólo devuelve uno de los 6 momentos válidos (mismo valor que `meal_slot`
  // en la biblioteca); "Comida N" no indica momento → ''.
  const inferMealSlot = (mt) => {
    const s = String(mt || '').toLowerCase();
    if (s.includes('desayuno')) return 'Desayuno';
    if (s.includes('almuerzo')) return 'Almuerzo';
    if (s.includes('cena')) return 'Cena';
    if (s.includes('post') && s.includes('entren')) return 'Post-Entreno';
    if (s.includes('snack') || s.includes('colaci') || s.includes('merienda')) {
      return /\bpm\b/.test(s) ? 'Snack PM' : 'Snack AM';
    }
    return '';
  };

  const [mealSlot, setMealSlot] = useState(inferMealSlot(mealTime));
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [recipes, setRecipes] = useState([]);
  const [picking, setPicking] = useState(null);

  const fetchList = async () => {
    // Sólo se listan recetas del momento seleccionado.
    if (!mealSlot) { setRecipes([]); return; }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (mealSlot) params.set('meal_slot', mealSlot);
      if (search) params.set('search', search);
      params.set('active_only', 'true');
      params.set('limit', '300');
      const { data } = await adminApi.get(`${API}/admin/recipe-library?${params.toString()}`);
      const items = (data?.items || data?.recipes || [])
        .filter((r) => String(r.meal_slot || '').toLowerCase() === mealSlot.toLowerCase());
      // Ordenar alfabéticamente por nombre
      items.sort((a, b) => (a.name || '').localeCompare(b.name || '', 'es'));
      setRecipes(items);
    } catch (e) {
      toast.error('Error cargando biblioteca: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchList();
  }, [mealSlot]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchList();
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl max-h-[85vh] rounded-2xl overflow-hidden flex flex-col"
        style={{ background: 'linear-gradient(180deg, #0f172a, #1e1b4b)', border: '1px solid rgba(255,255,255,0.1)' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="library-picker-modal"
      >
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <FaBookOpen className="text-pink-400" /> Elegir receta desde biblioteca
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">Se ajustará automáticamente a las calorías y macros de la cliente.</p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-white hover:bg-white/10"
            data-testid="close-library-picker"
          >
            <FaTimes />
          </button>
        </div>

        <div className="p-4 border-b border-white/10 flex flex-wrap gap-2">
          <select
            value={mealSlot}
            onChange={(e) => setMealSlot(e.target.value)}
            className="px-3 py-2 rounded bg-slate-800 text-white text-sm border border-slate-700 flex-shrink-0"
            data-testid="library-picker-mealslot"
          >
            {!mealSlot && <option value="" disabled>Selecciona un momento</option>}
            <option value="Desayuno">Desayuno</option>
            <option value="Snack AM">Snack AM</option>
            <option value="Almuerzo">Almuerzo</option>
            <option value="Snack PM">Snack PM</option>
            <option value="Cena">Cena</option>
            <option value="Post-Entreno">Post-Entreno</option>
          </select>
          <form onSubmit={handleSearchSubmit} className="flex-1 min-w-[200px] flex gap-2">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Buscar por nombre…"
              className="flex-1 px-3 py-2 rounded bg-slate-800 text-white text-sm border border-slate-700 focus:outline-none focus:border-pink-500"
              data-testid="library-picker-search"
            />
            <button
              type="submit"
              className="px-3 py-2 rounded bg-pink-500/20 text-pink-300 hover:bg-pink-500/30 text-sm flex items-center gap-1"
              data-testid="library-picker-search-btn"
            >
              <FaSearch className="text-xs" /> Buscar
            </button>
          </form>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="text-center text-slate-400 py-10">
              <FaSpinner className="animate-spin inline text-2xl" />
              <p className="mt-2 text-xs">Cargando biblioteca…</p>
            </div>
          ) : recipes.length === 0 ? (
            <p className="text-center text-slate-500 py-10 text-sm">
              No hay recetas en la biblioteca para este filtro.
            </p>
          ) : (
            <ul className="space-y-1.5" data-testid="library-picker-list">
              {recipes.map((r) => (
                <li
                  key={r.id}
                  className="rounded-lg p-2.5 bg-slate-800/60 border border-white/5 hover:border-pink-500/40 flex items-center justify-between gap-3 transition-all"
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-white font-semibold truncate" title={r.name}>
                      {r.name}
                    </p>
                    <div className="text-[11px] text-slate-400 flex flex-wrap gap-2 mt-0.5">
                      <span>🔥 {r.calories} kcal</span>
                      <span>🥩 {r.protein}g</span>
                      <span>🌾 {r.carbs}g</span>
                      <span>🥑 {r.fats}g</span>
                      {r.meal_slot && <span className="text-pink-300/80">· {r.meal_slot}</span>}
                    </div>
                  </div>
                  <button
                    onClick={async () => {
                      setPicking(r.id);
                      await onPick(r.id);
                      setPicking(null);
                    }}
                    disabled={!!picking}
                    className="px-3 py-1.5 rounded bg-pink-500 text-white text-xs font-semibold hover:bg-pink-600 disabled:opacity-50 flex items-center gap-1 flex-shrink-0"
                    data-testid={`library-pick-${r.id}`}
                  >
                    {picking === r.id ? <FaSpinner className="animate-spin" /> : <FaCheck />}
                    Elegir
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="p-3 border-t border-white/10 text-center text-[11px] text-slate-500">
          {recipes.length} receta{recipes.length !== 1 ? 's' : ''} disponibles · Ordenadas alfabéticamente
        </div>
      </div>
    </div>
  );
}
// ─────────────────────────────────────────────────────────
function WorkoutPanel({ workout, email, userSex, onRefresh }) {
  const [addingExerciseDay, setAddingExerciseDay] = useState(null);
  const [editingDay, setEditingDay] = useState(null);
  const [showTemplatesModal, setShowTemplatesModal] = useState(false);

  if (!workout) {
    return (
      <div className="text-center py-8 space-y-3">
        <p className="text-slate-400">No hay rutina generada todavía.</p>
        <button
          onClick={() => setShowTemplatesModal(true)}
          data-testid="apply-template-btn-empty"
          className="px-4 py-2 rounded bg-fuchsia-500 hover:bg-fuchsia-600 text-white text-sm font-semibold inline-flex items-center gap-2"
        >
          🎨 Aplicar plantilla para empezar
        </button>
        {showTemplatesModal && (
          <TemplatesModal
            email={email}
            onClose={() => setShowTemplatesModal(false)}
            onApplied={() => { setShowTemplatesModal(false); onRefresh(); }}
          />
        )}
      </div>
    );
  }

  // Metadata por día (day_number, focus, muscle_order) desde training_days
  const daysMeta = {};
  (workout.training_days || []).forEach((td) => {
    if (td && td.day_name) {
      daysMeta[td.day_name] = {
        day_number: td.day_number,
        focus: td.focus,
        muscle_order: td.muscle_order || [],
      };
    }
  });

  // Normalizar los ejercicios: pueden venir en 3 formatos:
  // 1) exercises: [{name, day_name, ...}] — FLAT, cada item con day_name propio
  // 2) exercises: [{day_name, exercises: [...]}] — NESTED por día
  // 3) days: [{day_name, exercises: [...]}] — DAYS separado
  const flatExercises = [];
  const rawExercises = workout.exercises || workout.days || [];
  rawExercises.forEach((item) => {
    if (item && typeof item === 'object' && Array.isArray(item.exercises)) {
      // Formato NESTED: expandir los ejercicios del día
      const dayName = item.day_name || 'Sin día';
      item.exercises.forEach((ex) => {
        flatExercises.push({ ...ex, day_name: ex.day_name || dayName });
      });
    } else if (item && item.name) {
      // Formato FLAT
      flatExercises.push(item);
    }
  });

  // Agrupar por day_name
  const grouped = {};
  flatExercises.forEach((ex, idx) => {
    const day = ex.day_name || 'Sin día';
    if (!grouped[day]) grouped[day] = [];
    grouped[day].push({ ...ex, _index: idx });
  });
  // Días sin ejercicios (p. ej. recién agregados) también se muestran; orden por day_number
  if (Array.isArray(workout.training_days)) {
    const ordered = [...workout.training_days]
      .filter((td) => td && td.day_name)
      .sort((a, b) => (a.day_number || 0) - (b.day_number || 0));
    ordered.forEach((td) => { if (!grouped[td.day_name]) grouped[td.day_name] = []; });
    const rank = Object.fromEntries(ordered.map((td, i) => [td.day_name, i]));
    const sortedEntries = Object.entries(grouped).sort(
      ([a], [b]) => (rank[a] ?? 9999) - (rank[b] ?? 9999)
    );
    Object.keys(grouped).forEach((k) => delete grouped[k]);
    sortedEntries.forEach(([k, v]) => { grouped[k] = v; });
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg p-3 bg-slate-800/60 border border-cyan-500/20">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1 min-w-0">
            <div className="text-xs text-slate-400 uppercase tracking-wider">Rutina activa</div>
            <div className="text-white font-bold">{workout.name}</div>
            <div className="text-xs text-slate-400">
              {(() => {
                const td = workout.training_days;
                const n = typeof td === 'number' ? td : Array.isArray(td) ? td.length : (parseInt(td) || Object.keys(grouped).length);
                return n;
              })()} días · Nivel: {workout.level || '—'} · Objetivo: {workout.goal || '—'}
            </div>
          </div>
          {/* Badge de distribución aplicada — verificación visual del bug de hombres */}
          {workout.splits_used && (
            <span
              data-testid="splits-used-badge"
              className={`shrink-0 px-2.5 py-1 rounded-full text-[11px] font-bold border ${
                workout.splits_used === 'MEN_SPLITS'
                  ? 'bg-blue-500/20 border-blue-400/50 text-blue-200'
                  : workout.splits_used === 'WOMEN_SPLITS'
                    ? 'bg-pink-500/20 border-pink-400/50 text-pink-200'
                    : 'bg-slate-500/20 border-slate-400/50 text-slate-200'
              }`}
              title={`Distribución aplicada (sexo normalizado: ${workout.sex_normalized || '—'})`}
            >
              {workout.splits_used === 'MEN_SPLITS' && '👨 MEN_SPLITS'}
              {workout.splits_used === 'WOMEN_SPLITS' && '👩 WOMEN_SPLITS'}
              {workout.splits_used === 'MIXED' && '⚖️ MIXED'}
            </span>
          )}
        </div>
      </div>
      <div className="flex justify-end mb-2">
        <button
          onClick={() => setShowTemplatesModal(true)}
          data-testid="apply-template-btn"
          className="px-3 py-1.5 rounded bg-fuchsia-500 hover:bg-fuchsia-600 text-white text-xs font-semibold flex items-center gap-1.5"
        >
          🎨 Aplicar plantilla
        </button>
      </div>
      {Object.entries(grouped).map(([day, exercises]) => {
        const meta = daysMeta[day] || { day_number: null, muscle_order: [] };
        return (
        <div key={day} className="rounded-xl p-4" style={{ background: 'rgba(6, 182, 212, 0.08)', border: '1px solid rgba(6, 182, 212, 0.25)' }}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-3 gap-2">
            <div className="flex-1 min-w-0">
              <h3 className="text-lg font-bold text-cyan-300 break-words sm:truncate">💪 {day}</h3>
              {meta.muscle_order && meta.muscle_order.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-1">
                  {meta.muscle_order.map((m, i) => (
                    <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-500/15 text-cyan-200 border border-cyan-500/30">
                      {m}
                    </span>
                  ))}
                </div>
              )}
            </div>
            <div className="flex gap-1">
              {meta.day_number != null && (
                <button
                  onClick={() => setEditingDay({
                    day_number: meta.day_number,
                    day_name: day,
                    muscle_order: meta.muscle_order || [],
                    focus: meta.focus || '',
                  })}
                  data-testid={`edit-day-btn-${day}`}
                  className="px-2 py-1.5 text-xs rounded bg-slate-700 text-white hover:bg-slate-600 flex items-center gap-1"
                  title="Editar nombre y músculos del día"
                >
                  <FaEdit className="text-[10px]" /> Editar día
                </button>
              )}
              <button
                onClick={() => setAddingExerciseDay(day)}
                data-testid={`add-exercise-btn-${day}`}
                className="px-3 py-1.5 text-xs rounded bg-cyan-500 text-white hover:bg-cyan-600 font-semibold flex items-center gap-1.5"
              >
                <FaPlus className="text-[10px]" /> Agregar ejercicio
              </button>
            </div>
          </div>
          <div className="space-y-2">
            {exercises.length === 0 && (
              <p className="text-sm text-slate-400 py-3 text-center" data-testid={`empty-day-${day}`}>
                Este día aún no tiene ejercicios. Usa "Agregar ejercicio".
              </p>
            )}
            {exercises.map((ex) => (
              <ExerciseCard
                key={ex._index}
                exercise={ex}
                exerciseIndex={ex._index}
                email={email}
                userSex={userSex}
                onRefresh={onRefresh}
              />
            ))}
          </div>
        </div>
        );
      })}
      <AddDayInline email={email} onSaved={onRefresh} />
      {addingExerciseDay && (
        <AddExerciseModal
          email={email}
          dayName={addingExerciseDay}
          userSex={userSex}
          onClose={() => setAddingExerciseDay(null)}
          onSaved={() => { setAddingExerciseDay(null); onRefresh(); }}
        />
      )}
      {editingDay && (
        <EditDayModal
          email={email}
          day={editingDay}
          onClose={() => setEditingDay(null)}
          onSaved={() => { setEditingDay(null); onRefresh(); }}
        />
      )}
      {showTemplatesModal && (
        <TemplatesModal
          email={email}
          onClose={() => setShowTemplatesModal(false)}
          onApplied={() => { setShowTemplatesModal(false); onRefresh(); }}
        />
      )}
    </div>
  );
}

function AddDayInline({ email, onSaved }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { toast.error('Escribe el nombre del día'); return; }
    setSaving(true);
    try {
      await adminApi.post(`${API}/admin/user-plans/${encodeURIComponent(email)}/workout-day`, { day_name: name.trim() });
      toast.success(`Día "${name.trim()}" agregado`);
      setName('');
      setOpen(false);
      onSaved();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="add-workout-day-btn"
        className="w-full py-3 rounded-xl border-2 border-dashed border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10 hover:border-cyan-400 text-sm font-semibold flex items-center justify-center gap-2 transition-colors"
      >
        <FaPlus className="text-xs" /> Agregar día
      </button>
    );
  }
  return (
    <div className="rounded-xl p-4 flex flex-col sm:flex-row gap-2" style={{ background: 'rgba(6, 182, 212, 0.08)', border: '1px solid rgba(6, 182, 212, 0.35)' }} data-testid="add-workout-day-form">
      <input
        autoFocus
        value={name}
        onChange={(e) => setName(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setOpen(false); }}
        placeholder="Ej: Día 4 · Pierna y glúteo"
        className="flex-1 px-3 py-2 rounded bg-slate-900 border border-slate-600 text-white text-sm focus:outline-none focus:border-cyan-400"
        data-testid="add-workout-day-input"
      />
      <div className="flex gap-2">
        <button onClick={save} disabled={saving} data-testid="add-workout-day-save"
          className="px-4 py-2 rounded bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-semibold disabled:opacity-50">
          {saving ? 'Guardando...' : 'Guardar día'}
        </button>
        <button onClick={() => { setOpen(false); setName(''); }} data-testid="add-workout-day-cancel"
          className="px-4 py-2 rounded bg-slate-700 hover:bg-slate-600 text-white text-sm">
          Cancelar
        </button>
      </div>
    </div>
  );
}

function ExerciseCard({ exercise, exerciseIndex, email, userSex, onRefresh }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [videoOptions, setVideoOptions] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(false);
  // series estructuradas por serie: [{reps, rest, load, method, method_name, method_description}]
  // Mismo formato que AddExerciseModal (~1155-1160). Back-compat: si series_detail
  // viene en formato LEGACY (array de strings o string única, ej. "3x12, 3x10"),
  // se convierte cada entrada en una fila editable en vez de perder la técnica por serie.
  const initialSeriesRows = (() => {
    const sd = exercise.series_detail;
    const DEFAULT_METHOD = { method: 'serie-normal', method_name: 'Serie Normal', method_description: 'Series tradicionales con descanso completo.' };
    if (Array.isArray(sd) && sd.length > 0) {
      if (sd[0] && typeof sd[0] === 'object') {
        // Formato estructurado (nuevo): preservar todos los campos, incluido load.
        return sd.map((s) => ({
          reps: s.reps || '',
          rest: s.rest || '',
          load: s.load || '',
          method: s.method || DEFAULT_METHOD.method,
          method_name: s.method_name || DEFAULT_METHOD.method_name,
          method_description: s.method_description || '',
        }));
      }
      // Formato LEGACY: array de strings (ej. ["1x15", "1x12"])
      return sd.map((s) => ({ reps: String(s), rest: '', load: '', ...DEFAULT_METHOD }));
    }
    if (typeof sd === 'string' && sd.trim()) {
      // Formato LEGACY: string única (ej. "3x12, 3x10")
      return sd.split(',').map((s) => s.trim()).filter(Boolean).map((s) => ({ reps: s, rest: '', load: '', ...DEFAULT_METHOD }));
    }
    return [{ reps: '', rest: '', load: '', ...DEFAULT_METHOD }];
  })();
  const [seriesRows, setSeriesRows] = useState(initialSeriesRows);
  const [methods, setMethods] = useState([]);
  const [form, setForm] = useState({
    name: exercise.name || '',
    muscle_group: exercise.muscle_group || '',
    section: exercise.section || exercise.exercise_type || '',
    execution: exercise.execution || '',
    objective: exercise.objective || '',
    video_url: exercise.video_url || '',
    video_id: exercise.video_id || '',
  });

  // Cargar técnicas disponibles al abrir el editor (mismo endpoint que AddExerciseModal ~1171)
  useEffect(() => {
    if (!editing) return;
    (async () => {
      try {
        const { data } = await adminApi.get(`${API}/admin/training-manager/methods?status=active&limit=50`);
        setMethods(data?.methods || []);
      } catch { setMethods([]); }
    })();
  }, [editing]);

  const updateSeriesRow = (idx, patch) => {
    setSeriesRows((prev) => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };

  const addSeriesRow = () => {
    const last = seriesRows[seriesRows.length - 1] || {};
    setSeriesRows([...seriesRows, {
      reps: last.reps || '10-12',
      rest: last.rest || '60s',
      load: '',
      method: last.method || 'serie-normal',
      method_name: last.method_name || 'Serie Normal',
      method_description: last.method_description || '',
    }]);
  };

  const removeSeriesRow = (idx) => {
    if (seriesRows.length <= 1) return;
    setSeriesRows(seriesRows.filter((_, i) => i !== idx));
  };

  const applySeriesMethodToAll = (slug) => {
    const m = methods.find((x) => x.slug === slug) || { slug, name: slug, description: '' };
    setSeriesRows((prev) => prev.map((r) => ({
      ...r, method: m.slug, method_name: m.name, method_description: m.description || '',
    })));
  };

  // Cargar videos disponibles cuando se abre el editor (filtrados por muscle_group + sexo del usuario)
  useEffect(() => {
    if (!editing) return;
    (async () => {
      setLoadingVideos(true);
      try {
        const params = new URLSearchParams();
        if (form.muscle_group) params.append('muscle_group', form.muscle_group);
        if (userSex) params.append('recommended_sex', userSex);
        const { data } = await adminApi.get(`${API}/admin/videos-for-swap?${params.toString()}`);
        setVideoOptions(data.videos || []);
      } catch (e) {
        console.error('Error loading videos:', e);
      } finally {
        setLoadingVideos(false);
      }
    })();
  }, [editing, form.muscle_group, userSex]);

  const save = async () => {
    setSaving(true);
    try {
      // Reconstruir series_detail estructurado — mismo formato que AddExerciseModal.save() (~1238-1246)
      const structuredSeries = seriesRows.map((r, i) => ({
        set: i + 1,
        reps: r.reps || '',
        rest: r.rest || '60',
        load: r.load || '',
        method: r.method || 'normal',
        method_name: r.method_name || '',
        method_description: r.method_description || '',
      }));
      const payload = {
        ...form,
        series_detail: structuredSeries,
      };
      await adminApi.put(`${API}/admin/user-plans/${encodeURIComponent(email)}/workout-exercise/${exerciseIndex}`, payload);
      toast.success('Ejercicio actualizado');
      setEditing(false);
      onRefresh();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!window.confirm(`¿Eliminar el ejercicio "${exercise.name}"?`)) return;
    try {
      await adminApi.delete(`${API}/admin/user-plans/${encodeURIComponent(email)}/workout-exercise/${exerciseIndex}`);
      toast.success('Ejercicio eliminado');
      onRefresh();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    }
  };

  return (
    <div className="rounded-lg p-3 bg-slate-800/60 border border-white/5" data-testid={`exercise-card-${exerciseIndex}`}>
      {!editing ? (
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 text-xs mb-1">
              {(exercise.section || exercise.exercise_type) && (
                <span className="px-2 py-0.5 rounded text-cyan-300 bg-cyan-500/10 font-bold uppercase">
                  {exercise.section || exercise.exercise_type}
                </span>
              )}
              {exercise.muscle_group && <span className="text-slate-400">{exercise.muscle_group}</span>}
            </div>
            <h4 className="font-bold text-white text-sm">
              {exercise.exercise_number ? `#${exercise.exercise_number} · ` : (exercise.order ? `#${exercise.order} · ` : '')}{exercise.name}
            </h4>

            {/* Series formateadas + técnica por serie (2026-02-27 restaurado)
                Reproduce el comportamiento del plan premium: cada serie muestra
                sus reps + descanso + un chip con el método/técnica de esa serie
                (Rest-Pause, Drop Set, Cluster, Tempo, etc.). */}
            {(() => {
              const sd = exercise.series_detail;
              // Formato NUEVO: array de dicts con method_name por serie
              if (Array.isArray(sd) && sd.length > 0 && typeof sd[0] === 'object' && sd[0] !== null) {
                return (
                  <div className="mt-2 space-y-1" data-testid={`ex-series-list-${exerciseIndex}`}>
                    {sd.map((s, si) => {
                      const method = s.method_name || s.method || '';
                      const isSpecial = method && method.toLowerCase() !== 'normal';
                      return (
                        <div key={si} className="flex items-center gap-2 text-xs">
                          <span className="text-cyan-300 font-mono w-8 flex-shrink-0">S{s.set ?? si + 1}</span>
                          <span className="text-slate-200 font-semibold">{s.reps || '-'} reps</span>
                          {s.rest && <span className="text-slate-500">· {s.rest}</span>}
                          {s.load && <span className="text-yellow-300">· {s.load}</span>}
                          {isSpecial && (
                            <span
                              className="ml-auto px-2 py-0.5 rounded text-[10px] font-bold uppercase bg-orange-500/15 text-orange-300 border border-orange-500/40"
                              title={s.method_description || method}
                            >
                              🎯 {method}
                            </span>
                          )}
                        </div>
                      );
                    })}
                    {/* Descripciones de las técnicas usadas (una por método único) */}
                    {(() => {
                      const seen = new Set();
                      const unique = [];
                      sd.forEach((s) => {
                        const name = s.method_name || s.method;
                        const desc = s.method_description;
                        if (name && name.toLowerCase() !== 'normal' && name.toLowerCase() !== 'serie normal' && desc && !seen.has(name)) {
                          seen.add(name);
                          unique.push({ name, desc });
                        }
                      });
                      if (unique.length === 0) return null;
                      return (
                        <div className="mt-2 space-y-1">
                          {unique.map((u, i) => (
                            <p key={i} className="text-[11px] text-orange-200/85 italic border-l-2 border-orange-500/40 pl-2">
                              <span className="font-semibold">🎯 {u.name}:</span> {u.desc}
                            </p>
                          ))}
                        </div>
                      );
                    })()}
                  </div>
                );
              }
              // Formato LEGACY: array de strings o series/reps planos
              const legacyLabel = (() => {
                if (Array.isArray(sd)) return sd.map((s) => String(s)).join(' · ');
                if (typeof sd === 'string') return sd;
                return exercise.series && exercise.reps ? `${exercise.series}x${exercise.reps}` : '';
              })();
              return (
                <div className="text-xs text-slate-300 mt-1" data-testid={`ex-series-legacy-${exerciseIndex}`}>
                  📊 {legacyLabel}
                </div>
              );
            })()}

            {exercise.execution && (
              <div className="text-xs text-orange-200/80 mt-1.5 italic border-l-2 border-orange-500/30 pl-2">
                🎯 <span className="font-semibold">Técnica:</span> {exercise.execution}
              </div>
            )}
          </div>
          <div className="flex gap-1">
            <button onClick={() => setEditing(true)} data-testid={`edit-exercise-${exerciseIndex}`} className="w-7 h-7 rounded flex items-center justify-center bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30" title="Editar">
              <FaEdit className="text-xs" />
            </button>
            <button onClick={remove} data-testid={`delete-exercise-${exerciseIndex}`} className="w-7 h-7 rounded flex items-center justify-center bg-red-500/20 text-red-300 hover:bg-red-500/30" title="Eliminar">
              <FaTrash className="text-xs" />
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Selector de video para reemplazar */}
          <div>
            <label className="text-[10px] text-slate-400 uppercase tracking-wider block mb-1">🎥 Video del ejercicio</label>
            <select
              value={form.video_id || ''}
              onChange={(e) => {
                const selected = videoOptions.find((v) => v.video_id === e.target.value || v.id === e.target.value);
                if (selected) {
                  setForm({
                    ...form,
                    name: selected.title,
                    video_url: selected.video_url,
                    video_id: selected.video_id || selected.id,
                    muscle_group: selected.muscle_group || form.muscle_group,
                  });
                }
              }}
              className="w-full px-2 py-1.5 rounded bg-slate-900 text-white text-sm border border-slate-700"
              data-testid={`select-video-${exerciseIndex}`}
            >
              <option value="">
                {loadingVideos ? '⏳ Cargando videos...' : `— Elegir video (${videoOptions.length} disponibles) —`}
              </option>
              {videoOptions.map((v) => (
                <option key={v.id} value={v.video_id || v.id}>
                  {v.title} {v.body_part ? `· ${v.body_part}` : ''} {v.location ? `· ${v.location}` : ''}
                </option>
              ))}
            </select>
            {form.video_url && (
              <a
                href={form.video_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[10px] text-cyan-400 hover:underline mt-1 inline-block"
              >
                🔗 Ver video actual
              </a>
            )}
          </div>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="w-full px-2 py-1 rounded bg-slate-900 text-white text-sm border border-slate-700"
            placeholder="Nombre del ejercicio"
            data-testid={`edit-exercise-name-${exerciseIndex}`}
          />
          <div className="grid grid-cols-2 gap-2">
            <input type="text" value={form.muscle_group} onChange={(e) => setForm({ ...form, muscle_group: e.target.value })} className="px-2 py-1 rounded bg-slate-900 text-white text-xs border border-slate-700" placeholder="Grupo muscular" />
            <input type="text" value={form.section} onChange={(e) => setForm({ ...form, section: e.target.value })} className="px-2 py-1 rounded bg-slate-900 text-white text-xs border border-slate-700" placeholder="Sección (ACTIVACIÓN, PRINCIPAL...)" />
          </div>
          {/* Editor de series estructuradas — técnica por serie (mismo patrón que AddExerciseModal ~1396-1474) */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-[10px] text-slate-400 uppercase tracking-wider">Series y técnica</label>
              <select
                onChange={(e) => e.target.value && applySeriesMethodToAll(e.target.value)}
                value=""
                className="px-2 py-1 rounded bg-orange-500/15 text-orange-200 border border-orange-500/40 text-[10px]"
                data-testid={`edit-exercise-apply-technique-all-${exerciseIndex}`}
                title="Aplica la técnica seleccionada a TODAS las series"
              >
                <option value="">🎯 Aplicar técnica a todas…</option>
                {methods.map((m) => (
                  <option key={m.slug} value={m.slug}>{m.name}</option>
                ))}
              </select>
            </div>
            <div className="rounded-lg border border-white/10 overflow-hidden" data-testid={`edit-exercise-series-editor-${exerciseIndex}`}>
              <div className="grid grid-cols-12 gap-1 px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400 bg-slate-900/60 font-bold">
                <div className="col-span-1">S#</div>
                <div className="col-span-2">Reps</div>
                <div className="col-span-2">Descanso</div>
                <div className="col-span-6">🎯 Técnica</div>
                <div className="col-span-1"></div>
              </div>
              {seriesRows.map((row, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-12 gap-1 px-2 py-1.5 items-center border-t border-white/5"
                  data-testid={`edit-exercise-series-row-${exerciseIndex}-${idx}`}
                >
                  <div className="col-span-1 text-cyan-300 font-mono text-xs font-bold">S{idx + 1}</div>
                  <div className="col-span-2">
                    <input
                      type="text"
                      value={row.reps}
                      onChange={(e) => updateSeriesRow(idx, { reps: e.target.value })}
                      className="w-full px-1.5 py-1 rounded bg-slate-900 text-white text-xs border border-slate-700"
                      placeholder="10-12"
                      data-testid={`edit-exercise-reps-${exerciseIndex}-${idx}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="text"
                      value={row.rest}
                      onChange={(e) => updateSeriesRow(idx, { rest: e.target.value })}
                      className="w-full px-1.5 py-1 rounded bg-slate-900 text-white text-xs border border-slate-700"
                      placeholder="60s"
                      data-testid={`edit-exercise-rest-${exerciseIndex}-${idx}`}
                    />
                  </div>
                  <div className="col-span-6">
                    <select
                      value={row.method || 'serie-normal'}
                      onChange={(e) => {
                        const m = methods.find((x) => x.slug === e.target.value) || { slug: e.target.value, name: e.target.value, description: '' };
                        updateSeriesRow(idx, {
                          method: m.slug,
                          method_name: m.name,
                          method_description: m.description || '',
                        });
                      }}
                      className={`w-full px-1.5 py-1 rounded text-xs border ${row.method && row.method !== 'serie-normal' ? 'bg-orange-500/15 text-orange-200 border-orange-500/40' : 'bg-slate-900 text-white border-slate-700'}`}
                      data-testid={`edit-exercise-method-${exerciseIndex}-${idx}`}
                    >
                      {row.method && !methods.some((m) => m.slug === row.method) && (
                        <option value={row.method}>{row.method_name || row.method}</option>
                      )}
                      {methods.map((m) => (
                        <option key={m.slug} value={m.slug}>{m.name}</option>
                      ))}
                    </select>
                    {row.method_description && row.method !== 'serie-normal' && (
                      <p className="mt-0.5 text-[10px] text-orange-200/70 italic pl-1 truncate" title={row.method_description}>
                        {row.method_description}
                      </p>
                    )}
                  </div>
                  <div className="col-span-1 text-right">
                    <button
                      onClick={() => removeSeriesRow(idx)}
                      disabled={seriesRows.length <= 1}
                      className="text-red-300 hover:bg-red-500/10 rounded px-1.5 py-0.5 text-xs disabled:opacity-30"
                      title="Quitar serie"
                      data-testid={`edit-exercise-remove-series-${exerciseIndex}-${idx}`}
                    >✕</button>
                  </div>
                </div>
              ))}
              <button
                onClick={addSeriesRow}
                className="w-full py-1.5 text-xs bg-slate-800/40 hover:bg-slate-800 text-cyan-300 border-t border-white/5 flex items-center justify-center gap-1"
                data-testid={`edit-exercise-add-series-${exerciseIndex}`}
              >
                <FaPlus className="text-[10px]" /> Agregar serie
              </button>
            </div>
          </div>
          <input
            type="text"
            value={form.execution}
            onChange={(e) => setForm({ ...form, execution: e.target.value })}
            className="w-full px-2 py-1 rounded bg-slate-900 text-white text-xs border border-slate-700"
            placeholder="Nota de ejecución (opcional, general para el ejercicio)"
          />
          <input
            type="text"
            value={form.objective}
            onChange={(e) => setForm({ ...form, objective: e.target.value })}
            className="w-full px-2 py-1 rounded bg-slate-900 text-white text-xs border border-slate-700"
            placeholder="Objetivo del ejercicio"
          />
          <div className="flex gap-2 justify-end">
            <button onClick={() => setEditing(false)} className="px-3 py-1 text-xs rounded bg-slate-700 text-white hover:bg-slate-600">
              Cancelar
            </button>
            <button onClick={save} disabled={saving} data-testid={`save-exercise-${exerciseIndex}`} className="px-3 py-1 text-xs rounded bg-green-600 text-white hover:bg-green-500 flex items-center gap-1">
              {saving ? <FaSpinner className="animate-spin" /> : <FaCheck />}
              Guardar
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Modal: Editar día de la rutina (nombre + músculos configurados)
// ─────────────────────────────────────────────────────────
function EditDayModal({ email, day, onClose, onSaved }) {
  const [dayName, setDayName] = useState(day.day_name || '');
  const [focus, setFocus] = useState(day.focus || '');
  const [muscles, setMuscles] = useState(day.muscle_order || []);
  const [newMuscle, setNewMuscle] = useState('');
  const [saving, setSaving] = useState(false);

  const MUSCLE_OPTIONS = [
    'gluteo', 'isquios', 'cuadriceps', 'aductores', 'abductores',
    'pantorrilla', 'core', 'lumbar',
    'espalda', 'pecho', 'hombros', 'biceps', 'triceps', 'antebrazos',
  ];

  const toggleMuscle = (m) => {
    if (muscles.includes(m)) setMuscles(muscles.filter((x) => x !== m));
    else setMuscles([...muscles, m]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await adminApi.patch(
        `${API}/admin/user-plans/${encodeURIComponent(email)}/workout-day/${day.day_number}`,
        {
          day_name: dayName.trim() || null,
          focus: focus.trim() || null,
          muscle_order: muscles,
        }
      );
      toast.success('Día actualizado');
      onSaved();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
      data-testid="edit-day-modal"
    >
      <div
        className="w-full max-w-lg max-h-[90vh] rounded-2xl overflow-hidden flex flex-col"
        style={{ background: 'linear-gradient(180deg, #0f172a, #1e1b4b)', border: '1px solid rgba(255,255,255,0.1)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <FaEdit className="text-cyan-400" /> Editar día
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Cambia el nombre y los músculos configurados para este día
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-white hover:bg-white/10" data-testid="close-edit-day">
            <FaTimes />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto">
          <div>
            <label className="text-xs text-slate-300 font-semibold uppercase tracking-widest">Nombre del día</label>
            <input
              type="text"
              value={dayName}
              onChange={(e) => setDayName(e.target.value)}
              placeholder="Ej: Día 1 - Pierna"
              className="w-full mt-1 px-3 py-2 rounded bg-slate-800 text-white border border-slate-700 focus:border-cyan-500 focus:outline-none"
              data-testid="edit-day-name-input"
            />
          </div>
          <div>
            <label className="text-xs text-slate-300 font-semibold uppercase tracking-widest">Foco (opcional)</label>
            <input
              type="text"
              value={focus}
              onChange={(e) => setFocus(e.target.value)}
              placeholder="Ej: Tren inferior / Push / Pull"
              className="w-full mt-1 px-3 py-2 rounded bg-slate-800 text-white border border-slate-700 focus:border-cyan-500 focus:outline-none"
              data-testid="edit-day-focus-input"
            />
          </div>
          <div>
            <label className="text-xs text-slate-300 font-semibold uppercase tracking-widest">Músculos del día</label>
            <p className="text-[10px] text-slate-500 mt-0.5">
              Selecciona los grupos que se trabajan hoy. Se usan como referencia visual y para próximas regeneraciones.
            </p>
            <div className="flex flex-wrap gap-1.5 mt-2" data-testid="edit-day-muscles">
              {MUSCLE_OPTIONS.map((m) => {
                const active = muscles.includes(m);
                return (
                  <button
                    key={m}
                    onClick={() => toggleMuscle(m)}
                    data-testid={`edit-day-muscle-${m}`}
                    className={`text-xs px-2.5 py-1 rounded-full border transition-all ${
                      active
                        ? 'bg-cyan-500 text-white border-cyan-400'
                        : 'bg-slate-800 text-slate-300 border-slate-700 hover:border-cyan-500/50'
                    }`}
                  >
                    {active && '✓ '}{m}
                  </button>
                );
              })}
            </div>
            <div className="flex gap-2 mt-2">
              <input
                type="text"
                value={newMuscle}
                onChange={(e) => setNewMuscle(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && newMuscle.trim()) {
                    setMuscles([...muscles, newMuscle.trim().toLowerCase()]);
                    setNewMuscle('');
                  }
                }}
                placeholder="+ Otro músculo (Enter)"
                className="flex-1 px-2 py-1 rounded bg-slate-800 text-white text-xs border border-slate-700"
              />
            </div>
          </div>
        </div>

        <div className="p-3 border-t border-white/10 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs rounded bg-slate-700 text-white hover:bg-slate-600">
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="save-edit-day"
            className="px-4 py-1.5 text-xs rounded bg-cyan-500 text-white hover:bg-cyan-600 disabled:opacity-50 font-semibold flex items-center gap-1"
          >
            {saving ? <FaSpinner className="animate-spin" /> : <FaCheck />}
            Guardar día
          </button>
        </div>
      </div>
    </div>
  );
}
function AddExerciseModal({ email, dayName, userSex, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exercises, setExercises] = useState([]);
  const [muscleFilter, setMuscleFilter] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);
  const [section, setSection] = useState('PRINCIPAL');
  const [methods, setMethods] = useState([]);
  // series estructuradas: [{reps, rest, method, method_name, method_description}]
  const [seriesRows, setSeriesRows] = useState([
    { reps: '10-12', rest: '60s', method: 'serie-normal', method_name: 'Serie Normal', method_description: 'Series tradicionales con descanso completo.' },
    { reps: '10-12', rest: '60s', method: 'serie-normal', method_name: 'Serie Normal', method_description: 'Series tradicionales con descanso completo.' },
    { reps: '10-12', rest: '60s', method: 'serie-normal', method_name: 'Serie Normal', method_description: 'Series tradicionales con descanso completo.' },
    { reps: '10-12', rest: '60s', method: 'serie-normal', method_name: 'Serie Normal', method_description: 'Series tradicionales con descanso completo.' },
  ]);

  const MUSCLE_OPTIONS = [
    'gluteo', 'isquios', 'cuadriceps', 'abductores', 'aductores', 'pantorrilla',
    'espalda', 'pecho', 'hombros', 'biceps', 'triceps', 'core', 'lumbar'
  ];

  // Cargar técnicas disponibles al abrir
  useEffect(() => {
    (async () => {
      try {
        const { data } = await adminApi.get(`${API}/admin/training-manager/methods?status=active&limit=50`);
        setMethods(data?.methods || []);
      } catch { setMethods([]); }
    })();
  }, []);

  const applyMethodToAll = (slug) => {
    const m = methods.find((x) => x.slug === slug) || { slug, name: slug, description: '' };
    setSeriesRows((prev) => prev.map((r) => ({
      ...r, method: m.slug, method_name: m.name, method_description: m.description || '',
    })));
  };

  const updateRow = (idx, patch) => {
    setSeriesRows((prev) => prev.map((r, i) => i === idx ? { ...r, ...patch } : r));
  };

  const addRow = () => {
    const last = seriesRows[seriesRows.length - 1] || {};
    setSeriesRows([...seriesRows, {
      reps: last.reps || '10-12',
      rest: last.rest || '60s',
      method: last.method || 'serie-normal',
      method_name: last.method_name || 'Serie Normal',
      method_description: last.method_description || '',
    }]);
  };

  const removeRow = (idx) => {
    if (seriesRows.length <= 1) return;
    setSeriesRows(seriesRows.filter((_, i) => i !== idx));
  };

  const fetchExercises = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (muscleFilter) params.append('muscle_group', muscleFilter);
      if (userSex) {
        const sx = String(userSex).toLowerCase();
        const norm = ['male', 'm', 'masculino'].includes(sx) ? 'hombre'
                   : ['female', 'f', 'femenino'].includes(sx) ? 'mujer'
                   : sx;
        params.append('recommended_sex', norm);
      }
      if (search.trim()) params.append('search', search.trim());
      const { data } = await adminApi.get(`${API}/admin/videos-for-swap?${params.toString()}`);
      setExercises(data.videos || []);
    } catch (e) {
      toast.error('Error cargando ejercicios: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchExercises(); /* eslint-disable-next-line */ }, [muscleFilter, userSex]);
  useEffect(() => {
    const t = setTimeout(() => { fetchExercises(); }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [search]);

  const save = async () => {
    if (!selected) { toast.error('Selecciona un ejercicio del catálogo'); return; }
    setSaving(true);
    try {
      // Convertir seriesRows a formato estructurado (dicts por serie)
      const structured = seriesRows.map((r, i) => ({
        set: i + 1,
        reps: r.reps || '',
        rest: r.rest || '60',
        load: '',
        method: r.method || 'normal',
        method_name: r.method_name || '',
        method_description: r.method_description || '',
      }));
      const payload = {
        name: selected.title,
        muscle_group: selected.muscle_group || '',
        section: section,
        video_url: selected.video_url || '',
        video_id: selected.video_id || selected.id || '',
        series_detail: structured,
        day_name: dayName,
      };
      await adminApi.post(`${API}/admin/user-plans/${encodeURIComponent(email)}/workout-exercise`, payload);
      toast.success(`✅ "${selected.title}" agregado al ${dayName}`);
      onSaved();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(6px)' }}
      onClick={onClose}
      data-testid="add-exercise-modal"
    >
      <div
        className="w-full max-w-2xl max-h-[90vh] rounded-2xl overflow-hidden flex flex-col"
        style={{ background: 'linear-gradient(180deg, #0f172a, #1e1b4b)', border: '1px solid rgba(255,255,255,0.1)' }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <FaPlus className="text-cyan-400" /> Agregar ejercicio a <span className="text-cyan-300">{dayName}</span>
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Solo se muestran los videos de tu biblioteca filtrados por sexo del cliente ({userSex || 'ambos'}). Los nombres se preservan tal cual están en la biblioteca.
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-white hover:bg-white/10" data-testid="close-add-exercise">
            <FaTimes />
          </button>
        </div>

        <div className="p-3 border-b border-white/10 flex flex-wrap gap-2">
          <select
            value={muscleFilter}
            onChange={(e) => setMuscleFilter(e.target.value)}
            className="px-3 py-2 rounded bg-slate-800 text-white text-sm border border-slate-700"
            data-testid="add-exercise-muscle-filter"
          >
            <option value="">Todos los músculos</option>
            {MUSCLE_OPTIONS.map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') fetchExercises(); }}
            placeholder="Buscar por nombre…"
            className="flex-1 min-w-[180px] px-3 py-2 rounded bg-slate-800 text-white text-sm border border-slate-700 focus:outline-none focus:border-cyan-500"
            data-testid="add-exercise-search"
          />
          <button
            onClick={fetchExercises}
            className="px-3 py-2 rounded bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 text-sm flex items-center gap-1"
            data-testid="add-exercise-search-btn"
          >
            <FaSearch className="text-xs" /> Buscar
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <div className="text-center text-slate-400 py-8">
              <FaSpinner className="animate-spin inline text-2xl" />
              <p className="mt-2 text-xs">Cargando catálogo…</p>
            </div>
          ) : exercises.length === 0 ? (
            <p className="text-center text-slate-500 py-8 text-sm">
              No hay ejercicios en el catálogo para este filtro.
            </p>
          ) : (
            <ul className="space-y-1.5" data-testid="add-exercise-list">
              {exercises.map((ex) => (
                <li
                  key={ex.id}
                  onClick={() => setSelected(ex)}
                  className={`rounded-lg p-2.5 border cursor-pointer transition-all ${
                    selected?.id === ex.id
                      ? 'bg-cyan-500/20 border-cyan-400/60'
                      : 'bg-slate-800/60 border-white/5 hover:border-cyan-400/40'
                  }`}
                  data-testid={`add-exercise-item-${ex.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm text-white font-semibold truncate" title={ex.title}>
                        {ex.title}
                      </p>
                      <div className="text-[11px] text-slate-400 flex flex-wrap gap-2 mt-0.5">
                        {ex.muscle_group && <span>💪 {ex.muscle_group}</span>}
                        {ex.equipment && <span>· {ex.equipment}</span>}
                        {ex.level && <span>· {ex.level}</span>}
                        <span className={ex.recommended_sex === 'mujer' ? 'text-pink-300' : ex.recommended_sex === 'hombre' ? 'text-blue-300' : 'text-slate-400'}>
                          · {ex.recommended_sex}
                        </span>
                      </div>
                    </div>
                    {selected?.id === ex.id && <FaCheck className="text-cyan-300 flex-shrink-0" />}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {selected && (
          <div className="p-3 border-t border-white/10 bg-slate-900/40 space-y-3">
            <div className="text-xs text-slate-300 flex items-center justify-between gap-2 flex-wrap">
              <div>Configuración para <span className="font-bold text-cyan-300">{selected.title}</span></div>
              <div className="flex items-center gap-2">
                <select
                  value={section}
                  onChange={(e) => setSection(e.target.value)}
                  className="px-2 py-1 rounded bg-slate-800 text-white text-xs border border-slate-700"
                  data-testid="add-exercise-section"
                >
                  <option value="ACTIVADOR">Activador</option>
                  <option value="PRINCIPAL">Principal</option>
                  <option value="SECUNDARIO">Secundario</option>
                  <option value="COMPLEMENTARIO">Complementario</option>
                </select>
                <select
                  onChange={(e) => e.target.value && applyMethodToAll(e.target.value)}
                  value=""
                  className="px-2 py-1 rounded bg-orange-500/15 text-orange-200 border border-orange-500/40 text-xs"
                  data-testid="add-exercise-apply-technique-all"
                  title="Aplica la técnica seleccionada a TODAS las series"
                >
                  <option value="">🎯 Aplicar técnica a todas…</option>
                  {methods.map((m) => (
                    <option key={m.slug} value={m.slug}>{m.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Editor de series estructuradas — técnica por serie */}
            <div className="rounded-lg border border-white/10 overflow-hidden" data-testid="add-exercise-series-editor">
              <div className="grid grid-cols-12 gap-1 px-2 py-1 text-[10px] uppercase tracking-wider text-slate-400 bg-slate-900/60 font-bold">
                <div className="col-span-1">S#</div>
                <div className="col-span-2">Reps</div>
                <div className="col-span-2">Descanso</div>
                <div className="col-span-6">🎯 Técnica (descripción abajo)</div>
                <div className="col-span-1"></div>
              </div>
              {seriesRows.map((row, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-12 gap-1 px-2 py-1.5 items-center border-t border-white/5"
                  data-testid={`add-exercise-series-row-${idx}`}
                >
                  <div className="col-span-1 text-cyan-300 font-mono text-xs font-bold">S{idx + 1}</div>
                  <div className="col-span-2">
                    <input
                      type="text"
                      value={row.reps}
                      onChange={(e) => updateRow(idx, { reps: e.target.value })}
                      className="w-full px-1.5 py-1 rounded bg-slate-800 text-white text-xs border border-slate-700"
                      placeholder="10-12"
                      data-testid={`add-exercise-reps-${idx}`}
                    />
                  </div>
                  <div className="col-span-2">
                    <input
                      type="text"
                      value={row.rest}
                      onChange={(e) => updateRow(idx, { rest: e.target.value })}
                      className="w-full px-1.5 py-1 rounded bg-slate-800 text-white text-xs border border-slate-700"
                      placeholder="60s"
                      data-testid={`add-exercise-rest-${idx}`}
                    />
                  </div>
                  <div className="col-span-6">
                    <select
                      value={row.method || 'serie-normal'}
                      onChange={(e) => {
                        const m = methods.find((x) => x.slug === e.target.value) || { slug: e.target.value, name: e.target.value, description: '' };
                        updateRow(idx, {
                          method: m.slug,
                          method_name: m.name,
                          method_description: m.description || '',
                        });
                      }}
                      className={`w-full px-1.5 py-1 rounded text-xs border ${row.method && row.method !== 'serie-normal' ? 'bg-orange-500/15 text-orange-200 border-orange-500/40' : 'bg-slate-800 text-white border-slate-700'}`}
                      data-testid={`add-exercise-method-${idx}`}
                    >
                      {methods.map((m) => (
                        <option key={m.slug} value={m.slug}>{m.name}</option>
                      ))}
                    </select>
                    {row.method_description && row.method !== 'serie-normal' && (
                      <p className="mt-0.5 text-[10px] text-orange-200/70 italic pl-1 truncate" title={row.method_description}>
                        {row.method_description}
                      </p>
                    )}
                  </div>
                  <div className="col-span-1 text-right">
                    <button
                      onClick={() => removeRow(idx)}
                      disabled={seriesRows.length <= 1}
                      className="text-red-300 hover:bg-red-500/10 rounded px-1.5 py-0.5 text-xs disabled:opacity-30"
                      title="Quitar serie"
                      data-testid={`add-exercise-remove-series-${idx}`}
                    >✕</button>
                  </div>
                </div>
              ))}
              <button
                onClick={addRow}
                className="w-full py-1.5 text-xs bg-slate-800/40 hover:bg-slate-800 text-cyan-300 border-t border-white/5 flex items-center justify-center gap-1"
                data-testid="add-exercise-add-series"
              >
                <FaPlus className="text-[10px]" /> Agregar serie
              </button>
            </div>
          </div>
        )}

        <div className="p-3 border-t border-white/10 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs rounded bg-slate-700 text-white hover:bg-slate-600">
            Cancelar
          </button>
          <button
            onClick={save}
            disabled={saving || !selected}
            data-testid="save-add-exercise"
            className="px-4 py-1.5 text-xs rounded bg-cyan-500 text-white hover:bg-cyan-600 disabled:opacity-50 font-semibold flex items-center gap-1"
          >
            {saving ? <FaSpinner className="animate-spin" /> : <FaCheck />}
            Agregar al día
          </button>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────
// Modal: Plantillas de rutina Fit Evolution
// ─────────────────────────────────────────────────────────
function TemplatesModal({ email, onClose, onApplied }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [replaceAll, setReplaceAll] = useState(false);  // Default seguro: solo estructura
  const [applying, setApplying] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await adminApi.get(`${API}/admin/fit-evolution/templates`);
        setTemplates(data.templates || []);
      } catch (e) {
        toast.error('Error cargando plantillas');
      } finally { setLoading(false); }
    })();
  }, []);

  const apply = async () => {
    if (!selected) { toast.error('Selecciona una plantilla'); return; }
    setApplying(true);
    try {
      const res = await adminApi.post(
        `${API}/admin/user-plans/${encodeURIComponent(email)}/apply-template`,
        { template_id: selected.id, replace_all: replaceAll }
      );
      toast.success(res.data.message || 'Plantilla aplicada');
      onApplied();
    } catch (e) {
      toast.error('Error: ' + (e.response?.data?.detail || e.message));
    } finally { setApplying(false); }
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4"
      style={{ background: 'rgba(0,0,0,0.9)', backdropFilter: 'blur(6px)' }}
      onClick={onClose} data-testid="templates-modal">
      <div className="w-full max-w-3xl max-h-[90vh] rounded-2xl overflow-hidden flex flex-col"
        style={{ background: 'linear-gradient(180deg, #1e1b4b, #0f172a)', border: '1px solid rgba(217,70,239,0.4)' }}
        onClick={(e) => e.stopPropagation()}>
        <div className="p-4 border-b border-white/10 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              🎨 Plantillas de rutina
            </h3>
            <p className="text-xs text-slate-400 mt-0.5">
              Selecciona una plantilla para aplicar como base a esta clienta
            </p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-white hover:bg-white/10">
            <FaTimes />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-3">
          {loading ? (
            <p className="text-center text-slate-400 py-8">Cargando…</p>
          ) : templates.length === 0 ? (
            <p className="text-center text-slate-500 py-8">
              No hay plantillas. Contacta al desarrollador para seed genérico.
            </p>
          ) : (
            <ul className="space-y-2" data-testid="templates-list">
              {templates.map((t) => (
                <li key={t.id}
                  onClick={() => setSelected(t)}
                  className={`p-3 rounded-lg border cursor-pointer transition-all ${
                    selected?.id === t.id
                      ? 'bg-fuchsia-500/20 border-fuchsia-400/60'
                      : 'bg-slate-800/60 border-white/5 hover:border-fuchsia-400/40'
                  }`}
                  data-testid={`template-item-${t.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <p className="text-white font-semibold">{t.name}</p>
                        {t.is_generic && (
                          <span className="text-[10px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 border border-cyan-500/30">
                            Genérica
                          </span>
                        )}
                      </div>
                      {t.description && <p className="text-xs text-slate-300 mt-1">{t.description}</p>}
                      <div className="text-[10px] text-slate-400 flex flex-wrap gap-2 mt-1.5">
                        <span>📅 {t.days_count} días</span>
                        <span>🎯 {(t.allowed_techniques || []).length} técnicas</span>
                      </div>
                      <div className="mt-2 flex flex-wrap gap-1">
                        {(t.days || []).map((d, i) => (
                          <span key={i} className="text-[10px] px-2 py-0.5 rounded bg-slate-700/60 text-slate-300">
                            {d.day_name}: {(d.muscle_order || []).join(', ')}
                          </span>
                        ))}
                      </div>
                    </div>
                    {selected?.id === t.id && <FaCheck className="text-fuchsia-300 flex-shrink-0" />}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {selected && (
          <div className="p-3 border-t border-white/10 bg-slate-900/40">
            <label className="flex items-start gap-2 text-xs text-slate-300 mb-2 cursor-pointer">
              <input
                type="checkbox"
                checked={replaceAll}
                onChange={(e) => setReplaceAll(e.target.checked)}
                data-testid="template-replace-all"
                className="mt-0.5"
              />
              <span>
                <strong className="text-red-300">Modo destructivo:</strong> reemplazar TODOS los ejercicios auto-generados
                <span className="block text-[10px] text-slate-500 mt-0.5">
                  Sin marcar (recomendado): solo actualiza nombre/foco/músculos del día, conserva los ejercicios existentes.
                </span>
              </span>
            </label>
          </div>
        )}

        <div className="p-3 border-t border-white/10 flex justify-end gap-2">
          <button onClick={onClose} className="px-3 py-1.5 text-xs rounded bg-slate-700 text-white hover:bg-slate-600">
            Cancelar
          </button>
          <button
            onClick={apply}
            disabled={applying || !selected}
            data-testid="apply-template-confirm"
            className="px-4 py-1.5 text-xs rounded bg-fuchsia-500 text-white hover:bg-fuchsia-600 disabled:opacity-50 font-semibold flex items-center gap-1"
          >
            {applying ? <FaSpinner className="animate-spin" /> : <FaCheck />}
            Aplicar plantilla
          </button>
        </div>
      </div>
    </div>
  );
}

